// src/pages/ServicePage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, User, BookOpen, Scale, Calendar } from 'lucide-react';

// Import services data map with information about each service
import servicesData from '../data/servicesData';
import ServiceIcon from '../components/ui/ServiceIcons';

const ServicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedServices, setRelatedServices] = useState([]);

  useEffect(() => {
    // Function to validate and normalize the service ID
    const normalizeServiceId = (serviceId) => {
      // Remove leading/trailing slashes and convert to lowercase
      return serviceId.replace(/^\/|\/$/g, '').toLowerCase();
    };

    // Normalize the incoming ID
    const normalizedId = normalizeServiceId(id);

    // Find the service in the data, allowing for some flexibility in matching
    const foundService = Object.entries(servicesData).find(
      ([key]) => normalizeServiceId(key) === normalizedId
    );

    if (foundService) {
      const [serviceId, serviceDetails] = foundService;
      setService(serviceDetails);
      
      // Get 3 related services (excluding current one)
      const allServiceIds = Object.keys(servicesData).filter(
        serviceKey => normalizeServiceId(serviceKey) !== normalizedId
      );
      
      const shuffled = [...allServiceIds].sort(() => 0.5 - Math.random());
      const selectedRelated = shuffled.slice(0, 3);
      
      setRelatedServices(selectedRelated.map(relatedId => ({
        id: relatedId,
        ...servicesData[relatedId]
      })));
    } else {
      // Redirect to 404 if service not found
      navigate('/not-found', { replace: true });
    }
    
    // Always set loading to false
    setLoading(false);
  }, [id, navigate]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  // If no service found, return null (will be redirected)
  if (!service) {
    return null;
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="bg-secondary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <Link to="/services" className="self-start text-white/80 hover:text-white flex items-center mb-6">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to All Services
            </Link>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">{service.title}</h1>
            <div className="w-16 h-1 bg-white mb-6"></div>
            <p className="text-xl text-white/90 max-w-3xl">{service.description}</p>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-secondary">Overview</h2>
              <p>{service.fullDescription}</p>
              
              <h2 className="text-secondary">Our Approach</h2>
              <p>{service.approach}</p>
              
              <h2 className="text-secondary">Key Areas of Focus</h2>
              <ul>
                {service.keyAreas.map((area, index) => (
                  <li key={index} className="flex items-start mb-2">
                    <CheckCircle className="h-5 w-5 text-secondary mr-2 flex-shrink-0 mt-1" />
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
              
              {service.recentCases && service.recentCases.length > 0 && (
                <>
                  <h2 className="text-secondary">Recent Success Stories</h2>
                  <ul className="space-y-4">
                    {service.recentCases.map((caseItem, index) => (
                      <li key={index} className="bg-gray-50 p-4 rounded-lg border-l-4 border-secondary/30">
                        {caseItem}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-xl p-6 sticky top-24 border border-secondary/20">
              <h3 className="text-xl font-bold text-secondary mb-4">Service Information</h3>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-center mb-2">
                    <div className="mr-3">
                      <ServiceIcon serviceId={id} className="h-8 w-8 text-secondary" />
                    </div>
                    <h4 className="font-medium text-gray-900">Practice Area</h4>
                  </div>
                  <p className="text-gray-600">Specialized legal services in {service.title}</p>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <BookOpen className="h-5 w-5 text-secondary mr-2" />
                    <h4 className="font-medium text-gray-900">Resources</h4>
                  </div>
                  <ul className="text-gray-600 space-y-1">
                    <li>
                      <Link to="#" className="text-secondary hover:text-secondary-dark">
                        Practice Area Brochure
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="text-secondary hover:text-secondary-dark">
                        Case Study Collection
                      </Link>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <Scale className="h-5 w-5 text-secondary mr-2" />
                    <h4 className="font-medium text-gray-900">Jurisdiction</h4>
                  </div>
                  <p className="text-gray-600">Comprehensive legal representation across Indian judicial forums</p>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <Calendar className="h-5 w-5 text-secondary mr-2" />
                    <h4 className="font-medium text-gray-900">Consultation</h4>
                  </div>
                  <p className="text-gray-600 mb-4">Schedule a confidential consultation with our experts</p>
                  <Link 
                    to="/contact" 
                    className="block w-full text-center px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors"
                  >
                    Request Consultation
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Related Services - Only show if we have related services */}
      {relatedServices.length > 0 && (
        <div className="bg-gray-50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-secondary mb-8 text-center">Related Services</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedServices.map((relatedService) => (
                <div 
                  key={relatedService.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden border-t-4 border-secondary"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{relatedService.title}</h3>
                    <p className="text-gray-600 mb-4">{relatedService.description}</p>
                    <Link to={`/services/${relatedService.id}`} className="text-secondary hover:text-secondary-dark">
                      Learn more →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicePage;